package at.theatervogelweide.admin.web.screens

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.Account
import at.theatervogelweide.admin.contract.AdminError
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.SignInRequest
import at.theatervogelweide.admin.web.AdminApi
import at.theatervogelweide.admin.web.components.AdminButton
import at.theatervogelweide.admin.web.components.AdminCard
import at.theatervogelweide.admin.web.components.AdminTextField
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.ButtonVariant
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private val signIn = Bem("admin-sign-in")
private val alert = Bem("admin-alert")

/** Only accounts with the admin role get in (#17); the server says so in its error, shown above the button. */
@Composable
fun SignInScreen(api: AdminApi, onSignedIn: (Account) -> Unit) {
    val scope = rememberCoroutineScope()
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var fieldErrors by remember { mutableStateOf(emptyList<FieldError>()) }
    var formError by remember { mutableStateOf<String?>(null) }
    var submitting by remember { mutableStateOf(false) }

    fun submit() {
        val request = SignInRequest(email.trim(), password)
        fieldErrors = request.validate()
        formError = null
        if (fieldErrors.isNotEmpty() || submitting) {
            return
        }

        submitting = true
        scope.launch {
            val result = runCatching { api.signIn(request) }
                .getOrElse { AdminResult.Failure(AdminError.internal()) }
            submitting = false
            when (result) {
                is AdminResult.Success -> onSignedIn(result.value)
                is AdminResult.Failure -> {
                    fieldErrors = result.error.fieldErrors
                    formError = result.error.message.takeIf { result.error.fieldErrors.isEmpty() }
                    password = ""
                }
            }
        }
    }

    fun errorFor(field: String) = fieldErrors.firstOrNull { it.field == field }?.message

    Div({ classes(signIn.block) }) {
        Div({ classes(signIn.element("panel").className) }) {
            Div({ classes(signIn.element("brand").className) }) {
                Span({
                    classes(signIn.element("mark").className)
                    attr("aria-hidden", "true")
                }) { Text("t") }
                Span({ classes(signIn.element("brand-text").className) }) {
                    Span({ classes(signIn.element("brand-name").className) }) { Text("Theater Vogelweide") }
                    Span({ classes(signIn.element("brand-area").className) }) { Text("Verwaltung") }
                }
            }

            Form(attrs = {
                attr("novalidate", "")
                onSubmit { event ->
                    event.preventDefault()
                    submit()
                }
            }) {
                AdminCard(
                    title = "Anmelden",
                    id = "anmelden",
                    titleLevel = 1,
                    footer = {
                        AdminButton(
                            variant = ButtonVariant.PRIMARY,
                            type = ButtonType.Submit,
                            disabled = submitting,
                            block = true,
                        ) { Text("Anmelden") }
                    },
                ) {
                    AdminTextField(
                        id = SignInRequest.EMAIL,
                        label = "E-Mail-Adresse",
                        value = email,
                        onValueChange = { email = it },
                        type = InputType.Email,
                        error = errorFor(SignInRequest.EMAIL),
                        autocomplete = "username",
                    )
                    AdminTextField(
                        id = SignInRequest.PASSWORD,
                        label = "Passwort",
                        value = password,
                        onValueChange = { password = it },
                        type = InputType.Password,
                        error = errorFor(SignInRequest.PASSWORD),
                        autocomplete = "current-password",
                    )
                    formError?.let {
                        P({
                            classes(alert.block)
                            attr("role", "alert")
                        }) { Text(it) }
                    }
                }
            }
        }
    }
}
